import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import useDebounce from "../../../../hooks/useDebounce";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalList,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg } from "../../../../utils/Helpers";
import { getAdminCompanyClients } from "../../../../network/apis/adminCompanyClients";
import {
  buildCompanyClientsListParams,
  getCompanyClientsListMeta,
} from "../../../Company-portal/Clients/clientMappers";
import { adminCompanyClientRoutes } from "../adminCompanyClientRoutes";

const SEARCH_DEBOUNCE_MS = 300;

const useAdminCompanyClientsList = (companyId) => {
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const { clients, shared } = messages[lang];
  const { canAdd } = useAdminModulePermission(
    companyPermissionModules.manageClients,
  );

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ currentPage: 1, count: 0, pages: 1 });
  const [isLoading, setIsLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      const params = buildCompanyClientsListParams({
        search: debouncedSearch,
        page,
        pageSize,
      });
      params.company_id = companyId;

      const response = await getAdminCompanyClients(params);
      const list = extractAdminPortalList(response);

      setRows(
        list.map((row) => ({
          ...row,
          country: row.country || {
            dial_code: String(row.dial_code || "").replace(/^\+/, ""),
          },
        })),
      );
      setMeta(getCompanyClientsListMeta(response));
    } catch (error) {
      setRows([]);
      setMeta({ currentPage: 1, count: 0, pages: 1 });
      const message =
        error?.response?.data?.message || shared.generalErrMsg;
      showErrorMsg(message);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, page, pageSize, debouncedSearch, shared.generalErrMsg]);

  useEffect(() => {
    if (!companyId) return;
    fetchClients();
  }, [companyId, fetchClients]);

  const totalClients = meta.count || 0;
  const currentPage = meta.currentPage || page;
  const paginationFrom =
    totalClients > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(currentPage * pageSize, totalClients);
  const paginationPages = meta.pages || 1;
  const isSearchEmpty =
    !isLoading && totalClients === 0 && Boolean(debouncedSearch.trim());

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const handleAddClient = useCallback(() => {
    if (!companyId) return;
    history.push(adminCompanyClientRoutes.create(companyId));
  }, [history, companyId]);

  const handleRowClick = useCallback(
    (client) => {
      if (!client?.id || !companyId) return;

      history.push({
        pathname: adminCompanyClientRoutes.view(companyId, client.id),
        state: { clientData: client, companyId },
      });
    },
    [history, companyId],
  );

  return useMemo(
    () => ({
      breadcrumbLabel: clients.title,
      title: clients.title,
      totalClients,
      canAdd,
      addClientLabel: clients.addClient.createClient,
      searchPlaceholder: clients.searchPlaceholder,
      searchValue,
      onSearchChange: handleSearchChange,
      onAddClient: handleAddClient,
      rows,
      page: currentPage,
      pageSize,
      paginationPages,
      paginationFrom,
      paginationTo,
      showingRangeTemplate: clients.showingRange,
      rowsPerPageLabel: shared.numberOfRows,
      onPageChange: handlePageChange,
      onPageSizeChange: handlePageSizeChange,
      onRowClick: handleRowClick,
      emptyTitle: clients.emptyTitle,
      emptyDescription: clients.emptyDescription,
      noDataDescription: clients.noDataDescription,
      isSearchEmpty,
      isLoading,
      tableLabels: {
        name: clients.listClients.name,
        email: clients.listClients.email,
        mobile: clients.listClients.mobile || clients.mobileNumber,
      },
    }),
    [
      clients,
      shared.numberOfRows,
      totalClients,
      canAdd,
      searchValue,
      handleSearchChange,
      handleAddClient,
      rows,
      currentPage,
      pageSize,
      paginationPages,
      paginationFrom,
      paginationTo,
      handlePageChange,
      handlePageSizeChange,
      handleRowClick,
      isSearchEmpty,
      isLoading,
    ],
  );
};

export default useAdminCompanyClientsList;
